import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdSearch } from "react-icons/md";

import { ReactComponent as DeleteRedIcon } from "../../../assets/delete_red_icon.svg";
import projectPulpBlue from "../../../assets/project-pulp-blue.svg";
import projectPulpWhite from "../../../assets/project-pulp-white.svg";
import TruncatedTextWithTooltip from "../../../components/TruncatedTextWithTooltip";
import { USER_TYPE } from "../../../constants/common";
import { useSnack } from "../../../hooks/useSnack";
import studentServices from "../../../services/studentServices";
import { formatCustomDate } from "../../../utils/formatCustomDate";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";
import Button from "../../Button";
import CustomModal from "../../CustomModal";
import LoadingSpinner from "../../LoadingSpinner";

const MyProjectsModal = forwardRef(({ onSubmit, onSubmitCreate }, ref) => {
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const navigate = useNavigateWithLang();
  const lang = getLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [projectData, setProjectData] = useState(null);
  const [currentProjectType, setCurrentProjectType] = useState(1); // 1: Playground, 3: Challenges

  useImperativeHandle(ref, () => ({
    openModal(projectData) {
      setProjectData(projectData);
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  useEffect(() => {
    if (isOpen) {
      fetchProjects(currentProjectType);
    }
  }, [isOpen]);

  useEffect(() => {
    const query = searchQuery.toLowerCase();
    setFilteredProjects(projects.filter((p) => p.name.toLowerCase().includes(query)));
  }, [searchQuery, projects]);

  const fetchProjects = async (type) => {
    setLoading(true);
    try {
      const response = await studentServices.getStudentProjects(type, projectData?.IDEType);
      setProjects(response);
      setFilteredProjects(response);
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (id) => {
    const response = await studentServices.getStudentProject(id);
    if (response.lesson_project_id) {
      navigate(`/${USER_TYPE.STUDENT.ROLE}/coding-area/challenge/${response.lesson_project_id}`);
    } else {
      navigate(`/${USER_TYPE.STUDENT.ROLE}/coding-area`);
    }

    onSubmit(response);
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleCreateProject = () => {
    navigate(`/${USER_TYPE.STUDENT.ROLE}/coding-area`);
    onSubmitCreate();
    setIsOpen(false);
    setSearchQuery("");
  };

  const handlePlaygroundClick = () => {
    setCurrentProjectType(1);
    fetchProjects(1);
  };

  const handleChallengesClick = () => {
    setCurrentProjectType(3);
    fetchProjects(3);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={() => {
        setSearchQuery("");
        setIsOpen(false);
      }}
      className="max-w-[45rem]"
      contentClassname="p-0 !py-0 !rounded-2xl"
    >
      <div className="flex max-h-[35rem]">
        {/* Sidebar */}
        <div className="w-1/4 border-r bg-blue-500 px-2 py-6">
          <img src={projectPulpWhite} className="mx-auto mb-6 mt-4" alt="" />
          <ul className="space-y-2">
            <li
              className={`cursor-pointer rounded px-3 py-2 text-center text-sm capitalize text-white transition-colors duration-150 hover:bg-blue-600 dark:hover:bg-gray-900 ${
                currentProjectType === 1 ? "bg-blue-700 font-medium text-blue-500" : ""
              }`}
              onClick={handlePlaygroundClick}
            >
              {t("builder_projects")}
            </li>
            <li
              className={`cursor-pointer rounded px-3 py-2 text-center text-sm capitalize text-white transition-colors duration-150 hover:bg-blue-600 dark:hover:bg-gray-900 ${
                currentProjectType === 3 ? "bg-blue-700 font-medium text-blue-500" : ""
              }`}
              onClick={handleChallengesClick}
            >
              {t("challenges_and_projects")}
            </li>
          </ul>
        </div>

        {/* Content */}
        <div className="custom-scrollbar flex min-h-[35rem] w-3/4 flex-col overflow-y-auto p-6 py-8">
          {/* Title + Search */}
          <div className="mb-4 mt-2 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-xl font-semibold text-blue-500">
              {t("my_projects")}
            </h3>
            <div className="flex justify-end gap-2">
              <Button onClick={handleCreateProject} label={t("create_new_project")} />
            </div>
          </div>
          <div className="relative mb-4 flex w-full items-center justify-between rounded-xl bg-[#ffffff] dark:bg-[#1F2937]">
            <MdSearch
              style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                // conditional left or right:
                [lang === "ar" ? "left" : "right"]: "0rem", // 'left-2' is 0.5rem
              }}
              className="h-8 w-8 rounded-xl bg-orange-500 p-1 text-xl text-white"
            />
            <input
              type="text"
              placeholder={t("search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border-gray-300 bg-gray-100 px-3 py-[6px] text-sm focus:border-blue-500 focus:outline-none dark:border-dark-border dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div className="flex flex-1 flex-col justify-between gap-4">
            {loading ? (
              <LoadingSpinner />
            ) : filteredProjects.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {filteredProjects.map((project) => (
                  <div
                    onClick={() => handleRowClick(project.id)}
                    key={project.id}
                    className="group relative cursor-pointer rounded-lg border bg-white p-4 text-center shadow-sm hover:border-blue-400 hover:bg-gray-50 dark:bg-gray-900"
                  >
                    <img src={projectPulpBlue} className="mx-auto mb-2" alt="" />

                    <TruncatedTextWithTooltip
                      text={project.name}
                      className="text-lg font-semibold text-gray-900 dark:text-white"
                    />

                    <p className="mt-1 text-xs font-light text-green-500">
                      {formatCustomDate(project?.date ? project?.date : project?.last_status?.at)}
                    </p>

                    {/* Action buttons (Edit + Delete) on hover */}
                    <div className="absolute bottom-[-25px] left-1/2 z-10 hidden -translate-x-1/2 transform gap-2 rounded-md px-2 py-1 group-hover:flex dark:bg-gray-800">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("Edit", project.id);
                        }}
                        className="rounded bg-blue-500 p-2 text-sm hover:bg-blue-700"
                      >
                        <FaEdit className="h-4 w-4 text-white" />
                      </button>
                      {/* <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("Delete", project.id);
                        }}
                        className="rounded bg-red-500 p-2 text-sm hover:bg-red-700"
                      >
                        <DeleteRedIcon className="text-white" />
                      </button> */}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t("no_projects_found")}</p>
            )}
          </div>
        </div>
      </div>
    </CustomModal>
  );
});

export default MyProjectsModal;
